/** Desktop views of the post log (M2.1): a sortable table and a week calendar. Phones keep the cards (PublicationRow). */
import { useMemo, useState } from "react";
import type { Publication } from "../../api/types";
import { Icon } from "../../components/Icon";
import { Badge, Button } from "../../components/ui";
import { dateTime, languageName } from "../../lib/format";
import { timeOnly } from "../../lib/tz";
import { openPublication } from "./PublicationRow";
import { platformName, stateChip } from "./pubState";

export const whenOf = (p: Publication) => p.published_at ?? p.scheduled_at;
const ms = (iso: string | null | undefined) => (iso ? Date.parse(iso) : Number.NaN);

type SortKey = "when" | "episode" | "series" | "language" | "channel" | "state" | "attempts";
const COLS: { key: SortKey; label: string }[] = [
  { key: "when", label: "Time" }, { key: "episode", label: "Episode" }, { key: "series", label: "Series" }, { key: "language", label: "Language" },
  { key: "channel", label: "Channel" }, { key: "state", label: "State" },
];
const value = (p: Publication, k: SortKey): string | number => {
  switch (k) {
    case "when": return ms(whenOf(p)) || 0;
    case "episode": return `${p.metadata.title || p.episode.title}`.toLowerCase();
    case "series": return `${p.series.title} ${String(p.episode.sequence).padStart(4, "0")}`.toLowerCase();
    case "language": return languageName(p.language_code);
    case "channel": return p.account_name.toLowerCase();
    case "state": return stateChip(p).label;
    case "attempts": return p.attempt_count;
  }
};

export function sortPublications(items: Publication[], key: SortKey, dir: "asc" | "desc"): Publication[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const x = value(a, key), y = value(b, key);
    return sign * (x < y ? -1 : x > y ? 1 : 0) || a.id.localeCompare(b.id);
  });
}

/** The post log as a table (desktop). Click a column to sort; click an episode for its detail. */
export function PubTable({ items, label, initial = { key: "when", dir: "desc" } }: { items: Publication[]; label: string; initial?: { key: SortKey; dir: "asc" | "desc" } }) {
  const [sort, setSort] = useState(initial);
  const rows = useMemo(() => sortPublications(items, sort.key, sort.dir), [items, sort]);
  // A render helper (not a component) so the header buttons keep keyboard focus across re-sorts.
  const th = (key: SortKey, text: string) => (
    <th key={key} scope="col" aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className="th-sort" onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : s.key === key ? "desc" : key === "when" ? "desc" : "asc" }))}>
        {text}{sort.key === key && <Icon name={sort.dir === "asc" ? "arrowUp" : "arrowDown"} size={14} />}
      </button>
    </th>
  );
  return (
    <div className="table-wrap">
      <table className="data-table pub-table" aria-label={label}>
        <thead>
          <tr>{COLS.map((c) => th(c.key, c.label))}<th scope="col">Link</th>{th("attempts", "Attempts")}</tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const chip = stateChip(p);
            const w = whenOf(p);
            return (
              <tr key={p.id}>
                <td className="nowrap"><time dateTime={w ?? undefined}>{w ? dateTime(w) : "As soon as possible"}</time></td>
                <td><button type="button" className="pub-open" onClick={() => openPublication(p.id)}><span className="muted">Ep {p.episode.sequence} · </span>{p.metadata.title || p.episode.title}</button></td>
                <td>{p.series.title}</td>
                <td>{languageName(p.language_code)}</td>
                <td>{p.account_name}</td>
                <td><Badge tone={chip.tone}>{chip.label}</Badge></td>
                <td>{p.platform_url ? <a href={p.platform_url} target="_blank" rel="noreferrer" aria-label={`Open “${p.episode.title}” on ${platformName(p.platform)}`}><Icon name="external" size={16} /> {platformName(p.platform)}</a> : <span className="muted">—</span>}</td>
                <td className="num">{p.attempt_count}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const DAY_MS = 86400_000;
const startOfDay = (t: number) => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); };

/** A 7-day calendar (desktop): one column per day, posts at their times; ← → move a week. */
export function WeekCalendar({ items, now = Date.now() }: { items: Publication[]; now?: number }) {
  const [offset, setOffset] = useState(0);
  const first = startOfDay(now) + offset * 7 * DAY_MS;
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(first); d.setDate(d.getDate() + i); return d.getTime(); });
  const asap = items.filter((p) => !whenOf(p));
  const label = (t: number) => new Date(t).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  const range = `${label(days[0]!)} – ${label(days[6]!)}`;
  return (
    <section className="week" aria-label={`Week of ${range}`}>
      <div className="week-nav">
        <Button size="sm" variant="ghost" icon="back" onClick={() => setOffset((o) => o - 1)} aria-label="Previous week" />
        <b>{range}</b>
        <Button size="sm" variant="ghost" icon="chevronRight" onClick={() => setOffset((o) => o + 1)} aria-label="Next week" />
        {offset !== 0 && <Button size="sm" variant="ghost" onClick={() => setOffset(0)}>This week</Button>}
      </div>
      {asap.length > 0 && <p className="muted small">{asap.length} post{asap.length === 1 ? "" : "s"} going out as soon as possible (not on the calendar).</p>}
      <div className="week-grid">
        {days.map((d) => {
          const next = new Date(d); next.setDate(next.getDate() + 1);
          const posts = items.filter((p) => { const t = ms(whenOf(p)); return t >= d && t < next.getTime(); }).sort((a, b) => ms(whenOf(a)) - ms(whenOf(b)));
          const today = d === startOfDay(now);
          return (
            <section key={d} className={`week-day ${today ? "is-today" : ""}`} aria-label={`${label(d)}${today ? " (today)" : ""}: ${posts.length} post${posts.length === 1 ? "" : "s"}`}>
              <h3 className="week-day-head">{label(d)}{today && <span className="sr-only"> (today)</span>}</h3>
              <ul>
                {posts.map((p) => {
                  const chip = stateChip(p);
                  return (
                    <li key={p.id}>
                      <button type="button" className={`week-post tone-${chip.tone}`} onClick={() => openPublication(p.id)} title={`${chip.label} · ${p.account_name}`}>
                        <span className="week-post-time">{timeOnly(whenOf(p))}</span>
                        <span className="week-post-title">Ep {p.episode.sequence} · {p.metadata.title || p.episode.title}</span>
                        <span className="week-post-meta">{p.account_name} · {chip.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </section>
  );
}
